// useTokenRefresh — proactive access-token renewal.
//
// Task 326.18 (finding WEB-001). What this replaces: the hook called refresh and then kept only the
// access token, throwing away the rotated refresh token — so from the FIRST proactive refresh, about
// 12 minutes into a session, the stored token was already dead. The next one failed, the operator
// got a "session expiring" toast, and the following API call logged them out mid-task.
//
// Two changes make that impossible now: rotation is the server's and the cookie's business, and the
// refresh goes through the same single-flight promise as the 401 path, so a proactive refresh and a
// 401 retry can never race each other for the same token.

import { useEffect } from 'react';
import { endSession, refreshSession } from '@/api/session';
import { useAuthStore } from '@/stores/authStore';
import { getTokenExpiry } from '@/shared/utils/jwt';

/** Renew at 80 % of the remaining lifetime, but never sooner than this. */
const MIN_DELAY_MS = 5_000;

export function useTokenRefresh(): void {
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    if (!token) return;

    const expiry = getTokenExpiry(token);
    if (!expiry) return;

    const delay = Math.max((expiry - Date.now()) * 0.8, MIN_DELAY_MS);

    const timer = setTimeout(() => {
      void refreshSession()
        .then((session) => {
          // Same user, same tenant, same grants: keep the cached permission codes rather than
          // making every renewal re-fetch /me (task 326.17).
          useAuthStore.getState().applySession(session, { keepPermissions: true });
          // The effect re-runs on the new token and schedules the next renewal.
        })
        .catch(() => {
          // The session is over. Say so and show a login form — the old code toasted "session
          // expiring" and left the operator working against a session that no longer existed.
          useAuthStore.getState().logout();
          endSession('expired');
        });
    }, delay);

    return () => clearTimeout(timer);
  }, [token]);
}
